import React from 'react'
import { Badge, BadgeArea, BaseButton, CardEventHeader, CardHeader, Carousel, CarouselHeader, DefaultCard, DefaultEventCard } from 'iebt-zing-storybook'
import { trans } from 'sharedKernel/i18n'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { Event } from 'data/contracts'
import { DeleteFormat, deleteProvider } from 'sharedKernel/api'
import { showToast } from 'components/toast'
import { showModal } from 'components/popup'
import { Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { hasAccess } from 'sharedKernel/auth/behavior'
import { SwiperWrapper } from 'components/swiper'
import { getHubRoute, removeTagsHTML } from 'sharedKernel/handlers'
import { ROUTES } from 'sharedKernel/constants'
import Tippy from '@tippyjs/react'
import { followCursor } from 'tippy.js'

export interface HubCompanyEventsCarouselListProps {
  eventList: Event[]
  setIsDelete?: React.Dispatch<React.SetStateAction<boolean>>
}

export const HubCompanyEventCarouselList = ({
  eventList,
  setIsDelete = () => { },
  ...props
}: HubCompanyEventsCarouselListProps): JSX.Element => {
  const navigate = useNavigate()
  const labels = {
    title: trans('Excluir Evento'),
    message: trans('Deseja realmente excluir esse Evento?'),
    confirm: { text: trans('Sim'), value: 'yes' },
    cancel: { text: trans('Não') },
    onSuccess: trans('Evento removido'),
    actionCardTitle: trans('Novo Evento'),
    edit: trans('Editar'),
    remove: trans('Excluir'),
    editTooltip: trans('Editar o Evento'),
    removeTooltip: trans('Excluir o Evento')
  }

  const onSuccessDeletion = async (success: any): Promise<void> => {
    showToast('success', labels.onSuccess)
    setIsDelete(true)
  }

  const { mutate: deleteChallenge } = deleteProvider(onSuccessDeletion)

  const handleDeleteChallenge = (id: string): void => {
    const request: DeleteFormat = {
      url: `/v1/hub/company/event/${id}`
    }
    deleteChallenge(request)
  }

  const createBadges = (event: Event, index: number): JSX.Element[] => {
    let { status } = event

    if (status === undefined) {
      status = ''
    }
    const isAvailable = (): any => {
      if (!event.isLaunched) {
        return 'pending'
      }
      const today = new Date()
      const endDateString = event.endDate?.split('T')[0]
      const endTimeString = event.endTime
      let endDateTime: Date
      if (endDateString !== null && endTimeString !== null) {
        endDateTime = new Date(`${endDateString}T${endTimeString}`)
      } else {
        endDateTime = new Date(endDateString)
      }
      const isEnded = endDateTime < today

      if (isEnded) {
        return 'closed'
      }
      return 'launched'
    }
    return [
      <Badge
        key={index}
        icon={['fas', 'signal']}
        status={isAvailable()}
      />
    ]
  }

  const events = eventList.map((event, index) => {
    const eventShortDescription = event.description
    const eventBadges = <BadgeArea>{createBadges(event, index)}</BadgeArea>
    const eventHeader = <CardEventHeader coverImage={event.style.coverImagePath} />
    const eventButtons = [
      <Tippy
        content={labels.editTooltip}
        followCursor={true}
        plugins={[followCursor]}
        animation="scale"
        arrow={false}
        theme="primary"
      >
        <div>
          <BaseButton
            key={index}
            variant="risk"
            size="medium"
            onClick={() => {
              navigate(`/hub/company/event/${event.id}/edit`)
            }}
            btnContent={labels.edit}
          />
        </div>
      </Tippy>,
      <Tippy
        content={labels.removeTooltip}
        followCursor={true}
        plugins={[followCursor]}
        animation="scale"
        arrow={false}
        theme="primary"
      >
        <div>
          <BaseButton
            key={index + 1}
            variant="risk-secondary"
            size="medium"
            onClick={() => {
              showModal('double', {
                title: labels.title,
                message: labels.message,
                buttons: [{ text: labels.confirm.text, value: labels.confirm.value }, { text: labels.cancel.text }],
                action: handleDeleteChallenge.bind(null, event.id)
              })
            }}
            btnContent={labels.remove}
          />
        </div>
      </Tippy>
    ]

    return (
      <DefaultEventCard
        title={event.name}
        shortDescription={removeTagsHTML(eventShortDescription)}
        badges={eventBadges}
        header={eventHeader}
        buttons={eventButtons}
        key={index}
        sendTo={() => {
          navigate(`/hub/company/event/${event.id}`)
        }}
      />
    )
  })

  const cardsList = [...events]

  const buildBreakpoints = (): any => {
    return {
      350: {
        slidesPerView: 1,
        spaceBetween: 20
      },
      900: {
        slidesPerView: 2,
        spaceBetween: 20
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 20
      },
      1500: {
        slidesPerView: 4,
        spaceBetween: 20
      },
      1800: {
        slidesPerView: 5,
        spaceBetween: 20
      },
      2200: {
        slidesPerView: 6,
        spaceBetween: 20
      },
      2800: {
        slidesPerView: 7,
        spaceBetween: 20
      }
    }
  }

  return (
    <>
      <CarouselHeader
        title={trans('Eventos')}
        info={trans('Veja os eventos cadastrados')}
        button={
          hasAccess('challenge', 'create') ? (
            <Button
              variant="primary"
              className="fw-bold fs-6 px-4 text-nowrap"
              style={{ letterSpacing: '1.5px' }}
              onClick={() => {
                navigate(getHubRoute(ROUTES.HUB.COMPANY.NEW_EVENT))
              }}
            >
              <FontAwesomeIcon icon={['fas', 'plus']} className="mr-3" />
              {trans('Criar Evento')}
            </Button>
          ) : (
            <></>
          )
        }
      />
      <SwiperWrapper breakpoints={buildBreakpoints()} carouselItems={cardsList} arrowColor='var(--primary)' />
    </>
  )
}
